import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Building2, Users, BarChart3, LogOut } from 'lucide-react';
import { toast } from 'sonner';

export default function SchoolRoleSelection() {
  const navigate = useNavigate();
  const { tenantName, tenantId, isSchoolAdmin, logout } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSchoolAdmin || !tenantId) {
      navigate('/');
      return;
    }
    fetchDepartments();
  }, [isSchoolAdmin, tenantId, navigate]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments?tenant_id=${tenantId}`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
      <div className="w-full max-w-6xl">
        {/* Header with School Name and Logout */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-sm text-slate-600">
            Logged in as: <span className="font-semibold text-slate-900">{tenantName}</span>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </Button>
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 mb-6 shadow-xl">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Review Management System
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            For School Promoters - Track KRAs, KPIs, and departmental performance with comprehensive reviews and analytics
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Promoter's Dashboard Card */}
          <Card 
            className="p-8 bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
            onClick={() => navigate('/school-admin')}
            data-testid="promoter-dashboard-card"
          >
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-12 h-12 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Promoter's Dashboard
                </h2>
                <p className="text-slate-600 mb-6">
                  Manage KRAs/KPIs, conduct reviews, and monitor all department performance
                </p>
                <Button 
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-6 rounded-xl shadow-lg"
                  data-testid="access-promoter-dashboard"
                >
                  Access Promoter's Dashboard
                </Button>
              </div>
            </div>
          </Card>

          {/* Departments Dashboard Card */}
          <Card className="p-8 bg-white border-2 border-slate-200 hover:border-purple-400 hover:shadow-2xl transition-all duration-300 group">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Users className="w-12 h-12 text-white" />
              </div>
              <div className="w-full">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Departments Dashboard
                </h2>
                <p className="text-slate-600 mb-6">
                  View department's KRAs/KPIs, review history, SOPs and performance analytics
                </p>
                
                {loading ? (
                  <div className="text-slate-500">Loading departments...</div>
                ) : (
                  <select
                    className="w-full p-4 border-2 border-slate-300 rounded-xl mb-4 bg-white text-slate-900 font-medium focus:border-purple-500 focus:outline-none transition-colors"
                    onChange={(e) => e.target.value && navigate(`/department/${e.target.value}`)}
                    defaultValue=""
                    data-testid="department-select"
                  >
                    <option value="" disabled>Select Your Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-12 text-center text-sm text-slate-500">
          <p>Built for organizational excellence and continuous improvement</p>
        </div>
      </div>
    </div>
  );
}
